// src/utils/financialAnalystApi.js
// API functions for the Financial Analyst feature
import { api } from './api';
import { FASTIFY_BASE_URL } from '../config/apiConfig';
import API_ENDPOINTS from '../config/apiConfig';

const FA_BASE = `${FASTIFY_BASE_URL}/financial-analyst`;

// ===================== PACKAGES =====================

export const createPackage = async (data) => {
  const response = await api.post(`${FA_BASE}/packages`, data);
  return response.data;
};

export const getPackage = async (id, include = '') => {
  const params = include ? `?include=${include}` : '';
  const response = await api.get(`${FA_BASE}/packages/${id}${params}`);
  return response.data;
};

export const listPackages = async (params = {}) => {
  const response = await api.get(`${FA_BASE}/packages`, { params });
  return response.data;
};

export const updatePackage = async (id, data) => {
  const response = await api.put(`${FA_BASE}/packages/${id}`, data);
  return response.data;
};

export const lockPackage = async (id) => {
  const response = await api.post(`${FA_BASE}/packages/${id}/lock`);
  return response.data;
};

export const generatePackage = (packageId, onProgress, options = null) => {
  const url = `${FA_BASE}/packages/${packageId}/generate`;
  // options: { materiality: { attention: { amount, pct, join }, critical: {...} } }
  return fetchSSE(url, 'POST', options, onProgress);
};

// ===================== CRITIQUE LINES =====================

export const getCritiqueLines = async (packageId, params = {}) => {
  const response = await api.get(`${FA_BASE}/packages/${packageId}/critique-lines`, { params });
  return response.data;
};

export const updateCritiqueLine = async (id, data) => {
  const response = await api.put(`${FA_BASE}/critique-lines/${id}`, data);
  return response.data;
};

export const getCritiqueLineGlDetail = async (lineId) => {
  const response = await api.get(`${FA_BASE}/critique-lines/${lineId}/gl-detail`);
  return response.data;
};

export const refineCritiqueLine = async (lineId, instruction = null) => {
  const response = await api.post(`${FA_BASE}/critique-lines/${lineId}/refine`, { instruction });
  return response.data;
};

// ===================== CRITIQUE NOTES =====================

export const createCritiqueNote = async (lineId, data) => {
  const response = await api.post(`${FA_BASE}/critique-lines/${lineId}/notes`, data);
  return response.data;
};

export const updateCritiqueNote = async (noteId, data) => {
  const response = await api.put(`${FA_BASE}/critique-notes/${noteId}`, data);
  return response.data;
};

export const deleteCritiqueNote = async (noteId) => {
  const response = await api.delete(`${FA_BASE}/critique-notes/${noteId}`);
  return response.data;
};

// ===================== PACKAGE NOTES =====================

export const createPackageNote = async (packageId, data) => {
  const response = await api.post(`${FA_BASE}/packages/${packageId}/notes`, data);
  return response.data;
};

export const getPackageNotes = async (packageId, params = {}) => {
  const response = await api.get(`${FA_BASE}/packages/${packageId}/notes`, { params });
  return response.data;
};

// ===================== NARRATIVES =====================

export const getNarratives = async (packageId) => {
  const response = await api.get(`${FA_BASE}/packages/${packageId}/narratives`);
  return response.data;
};

export const createNarrative = async (packageId, data) => {
  const response = await api.post(`${FA_BASE}/packages/${packageId}/narratives`, data);
  return response.data;
};

export const updateNarrative = async (narrativeId, data) => {
  const response = await api.put(`${FA_BASE}/narratives/${narrativeId}`, data);
  return response.data;
};

export const narrativeAiAction = async (narrativeId, data) => {
  const response = await api.post(`${FA_BASE}/narratives/${narrativeId}/ai-action`, data);
  return response.data;
};

export const getNarrativeVersions = async (narrativeId) => {
  const response = await api.get(`${FA_BASE}/narratives/${narrativeId}/versions`);
  return response.data;
};

export const revertNarrative = async (narrativeId, version) => {
  const response = await api.post(`${FA_BASE}/narratives/${narrativeId}/revert`, { version });
  return response.data;
};

export const harmonizeNarratives = async (packageId, data) => {
  const response = await api.post(`${FA_BASE}/packages/${packageId}/harmonize`, data);
  return response.data;
};

// ===================== AI ACTIONS =====================

export const regenerateSummary = async (packageId) => {
  const response = await api.post(`${FA_BASE}/packages/${packageId}/regenerate-summary`);
  return response.data;
};

export const reanalyzePackage = (packageId, onProgress) => {
  const url = `${FA_BASE}/packages/${packageId}/reanalyze`;
  return fetchSSE(url, 'POST', null, onProgress);
};

// ===================== EXPORT =====================

export const exportPackage = async (packageId, data) => {
  const response = await api.post(`${FA_BASE}/packages/${packageId}/export`, data, {
    responseType: 'blob',
  });

  // Create a download link from the blob
  const blob = response.data;
  const contentDisposition = response.headers['content-disposition'] || '';
  const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
  const filename = filenameMatch ? filenameMatch[1] : `package-${packageId}.${data.format || 'pdf'}`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

// ===================== REPORT TEMPLATES (Azure Functions — BASE_URL) =====================

export const listTemplates = async (orgId) => {
  const response = await api.get(`${API_ENDPOINTS.REPORT_TEMPLATES}?orgId=${orgId}`);
  return response.data;
};

export const getTemplate = async (id, orgId) => {
  const response = await api.get(`${API_ENDPOINTS.REPORT_TEMPLATES}?id=${id}&orgId=${orgId}`);
  return response.data;
};

export const createTemplate = async (data) => {
  const response = await api.post(API_ENDPOINTS.REPORT_TEMPLATES, data);
  return response.data;
};

export const updateTemplate = async (id, data) => {
  const response = await api.put(API_ENDPOINTS.REPORT_TEMPLATES, { id, ...data });
  return response.data;
};

export const deleteTemplate = async (id, orgId) => {
  const response = await api.delete(`${API_ENDPOINTS.REPORT_TEMPLATES}?id=${id}&orgId=${orgId}`);
  return response.data;
};

export const getDepartments = async (orgId, locationId = null) => {
  let url = `${API_ENDPOINTS.REPORT_TEMPLATE_DEPARTMENTS}?orgId=${orgId}`;
  if (locationId) url += `&locationId=${locationId}`;
  const response = await api.get(url);
  return response.data;
};

// ===================== REPORT GENERATION (Azure Functions — BASE_URL) =====================

export const generateFinancialReport = async (orgId, locationId, periodStart, periodEnd, templateId, forecastVersionId = null) => {
  const params = new URLSearchParams({ orgId, locationId, periodStart, periodEnd, templateId });
  if (forecastVersionId) params.set('forecastVersionId', forecastVersionId);
  const response = await api.get(`${API_ENDPOINTS.GENERATE_FINANCIAL_REPORT}?${params}`);
  return response.data;
};

// ===================== SSE Helper =====================

async function fetchSSE(url, method, body, onEvent) {
  const accessTokenKey = Object.keys(localStorage).find(key => key.endsWith('.accessToken'));
  const idTokenKey = Object.keys(localStorage).find(key => key.endsWith('.idToken'));

  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (accessTokenKey) headers['Authorization'] = `Bearer ${localStorage.getItem(accessTokenKey)}`;
  if (idTokenKey) headers['x-id-token'] = localStorage.getItem(idTokenKey);

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (onEvent) onEvent(data);
        } catch (e) {
          // ignore parse errors
        }
      }
    }
  }
}
